import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Check, Search, Barcode } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { useToast } from "@/components/ui/use-toast";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// Search configuration
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 1;

const ProductSearch = ({ products, onSelect, className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const searchInputRef = useRef(null);
  const { toast } = useToast();

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode) => {
    setIsScanning(true);
    try {
      // Try to find product by barcode using dedicated endpoint
      const response = await axios.get(
        `${API_URL}products/barcode/${encodeURIComponent(barcode)}`,
        { withCredentials: true }
      );

      if (response.data.success && response.data.data) {
        const product = response.data.data;

        // Check if product is out of stock
        if (product.stockQuantity <= 0) {
          toast({
            title: "Out of Stock",
            description: `${product.title} is currently out of stock`,
            variant: "destructive"
          });
        } else {
          // Auto-select the product
          onSelect(product);
          toast({
            title: "Product Found",
            description: `${product.title} added to cart`,
            className: "bg-green-50 dark:bg-green-900/30 border-green-200"
          });
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast({
          title: "Product Not Found",
          description: `No product found with barcode: ${barcode}`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to lookup product",
          variant: "destructive"
        });
      }
    } finally {
      setIsScanning(false);
    }
  }, [onSelect, toast]);

  // Enable barcode scanner
  useBarcodeScanner(handleBarcodeScan, {
    enabled: scannerEnabled,
    minLength: 3,
    timeout: 50
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim().toLowerCase());
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized search results with advanced filtering
  const filteredResults = useMemo(() => {
    // Only search if term meets minimum length
    if (debouncedTerm.length < MIN_SEARCH_LENGTH) return [];

    // Advanced multi-field search
    return (
      products
        ?.filter(
          (product) =>
            // Check if product is active (status 1)
            (product.status === 1) &&
            // Match product title
            (product.title?.toLowerCase().includes(debouncedTerm) ||
              // Match product ID
              product.id?.toString().includes(debouncedTerm) ||
              // Match barcode
              product.barcode?.toLowerCase().includes(debouncedTerm) ||
              // Match SKU (inside variations)
              (product.variations &&
                typeof product.variations === 'object' &&
                product.variations.sku?.toLowerCase().includes(debouncedTerm))
            )
        )
        .slice(0, 10) || []
    ); // Limit to 10 results
  }, [debouncedTerm, products]);

  // Handle product selection
  const handleSelect = useCallback(
    (product) => {
      onSelect(product);
      setSearchTerm("");
    },
    [onSelect]
  );

  return (
    <div className={cn("w-full relative", className)}>
      <div className="relative">
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={isScanning ? "Scanning barcode..." : "Search by name, ID, or scan barcode..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={cn("w-full pl-10 pr-10", isScanning && "border-blue-500")}
          disabled={isScanning}
        />
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          size={20}
        />
        {scannerEnabled && (
          <Barcode
            className={cn(
              "absolute right-3 top-1/2 transform -translate-y-1/2",
              isScanning ? "text-blue-500 animate-pulse" : "text-green-500"
            )}
            size={20}
          />
        )}
      </div>

      {/* Search Results Dropdown */}
      {searchTerm.length >= MIN_SEARCH_LENGTH && (
        <div className="absolute z-50 w-full mt-1 bg-background border shadow-lg max-h-[300px] overflow-auto">
          {/* No Results State */}
          {filteredResults.length === 0 && (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No products found
            </div>
          )}

          {/* Results List */}
          {filteredResults.map((product) => { // Use map block for logic
            const isOutOfStock = (product.stockQuantity || 0) <= 0;
            return (
              <div
                key={product.id}
                className={cn(
                  "flex items-center justify-between w-full px-3 py-2 transition-colors border-b last:border-0",
                  isOutOfStock ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-800" : "cursor-pointer hover:bg-accent"
                )}
                onClick={() => {
                  if (!isOutOfStock) handleSelect(product);
                }}
                role="button"
                tabIndex={isOutOfStock ? -1 : 0}
              >
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 opacity-0" />
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {product.title}
                      {isOutOfStock && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">OUT OF STOCK</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {product.id} | Stock: <span className={isOutOfStock ? "text-red-500 font-bold" : "text-green-600"}>{product.stockQuantity || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">GHS {parseFloat(product.current_price || 0).toFixed(2)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
